import React, { useState } from "react";
import Title from "antd/es/typography/Title";
import Paragraph from "antd/es/typography/Paragraph";
import {
  EyeOutlined,
  EyeInvisibleOutlined,
  BankOutlined,
  LockOutlined,
  EnvironmentOutlined,
  CheckCircleFilled,
  UserOutlined,
  SafetyCertificateOutlined,
} from "@ant-design/icons";
import { liveDemoSteps } from "../../../../Utils/staticData";
import "./index.scss";

const CardChrome = () => (
  <div className="demo-card-chrome" aria-hidden="true">
    <span />
    <span />
    <span />
  </div>
);

const LiveDemo = () => {
  const [activeStep, setActiveStep] = useState(0);
  const step = liveDemoSteps[activeStep];
  const { leader, recruiter, columnSubtext } = step;

  // Acting on a card walks the demo forward, so the flow can be played through
  // from either side rather than only via the stepper.
  const goToNextStep = () =>
    setActiveStep((current) =>
      current < liveDemoSteps.length - 1 ? current + 1 : current
    );

  return (
    <div className="live-demo-wrapper" id="live-demo">
      <div className="live-demo-container">
        <div className="section-header">
          <span className="section-label">Live Demo</span>
          <Title level={2} className="section-heading">
            Two Perspectives. One Seamless Match.
          </Title>
          <Paragraph className="section-subtext">
            See exactly what each side can see — and how both identities stay
            protected until there's a genuine, mutual fit.
          </Paragraph>
        </div>

        <div className="live-demo-stepper" role="tablist">
          {liveDemoSteps.map((s, index) => (
            <button
              type="button"
              key={s.id}
              role="tab"
              aria-selected={index === activeStep}
              className={`live-demo-step${index === activeStep ? " active" : ""}`}
              onClick={() => setActiveStep(index)}
            >
              <span className="live-demo-step-index">{index + 1}</span>
              {s.label}
            </button>
          ))}
        </div>

        <div className="live-demo-columns">
          {/* THE CANDIDATE SEES */}
          <div className="live-demo-column">
            <div className="live-demo-column-header">
              <EyeOutlined />
              <span>The Candidate Sees</span>
            </div>
            <p className="live-demo-column-sub">{columnSubtext.leader}</p>

            <div className="live-demo-card leader-card">
              <div className="live-demo-card-body">
                <CardChrome />
                <div className="leader-card-top">
                  <div className="leader-card-icon">
                    <BankOutlined />
                  </div>
                  <div className="leader-card-lock">
                    <LockOutlined />
                  </div>
                </div>

                <h3 className="leader-card-title">{leader.title}</h3>
                <div className="leader-card-location">
                  <EnvironmentOutlined /> {leader.location}
                </div>

                <div className="leader-card-tags">
                  {leader.tags.map((tag) => (
                    <span className="tag-pill" key={tag}>
                      {tag}
                    </span>
                  ))}
                </div>

                <div className="leader-card-about">
                  <div className="about-heading">{leader.aboutHeading}</div>
                  <p>{leader.bullets[0]}</p>
                  <ul>
                    {leader.bullets.slice(1).map((bullet) => (
                      <li key={bullet}>{bullet}</li>
                    ))}
                  </ul>
                </div>
              </div>

              <button
                type="button"
                className={`leader-card-action leader-card-action--${leader.actionState}`}
                disabled={leader.actionState !== "default"}
                onClick={goToNextStep}
              >
                {leader.actionState === "sent" && <CheckCircleFilled />}
                {leader.actionLabel}
              </button>
              <p className="live-demo-note">{leader.note}</p>

              <div className="live-demo-card-footer">
                <EyeInvisibleOutlined /> {leader.footerBadge}
              </div>
            </div>
          </div>

          {/* THE RECRUITER SEES */}
          <div className="live-demo-column">
            <div className="live-demo-column-header">
              <EyeInvisibleOutlined />
              <span>The Recruiter Sees</span>
            </div>
            <p className="live-demo-column-sub">{columnSubtext.recruiter}</p>

            <div className="live-demo-card recruiter-card">
              <div className="live-demo-card-body">
                <CardChrome />

                {recruiter.state === "locked" ? (
                  <div className="recruiter-locked">
                    <div className="skeleton-rows" aria-hidden="true">
                      <span />
                      <span />
                      <span />
                    </div>
                    <div className="recruiter-lock-icon">
                      <LockOutlined />
                    </div>
                    <h3>{recruiter.heading}</h3>
                    <p>{recruiter.copy}</p>
                  </div>
                ) : (
                  <div className="recruiter-candidate">
                    <div className="candidate-identity">
                      <div className="candidate-avatar">
                        <UserOutlined />
                      </div>
                      <div className="candidate-identity-text">
                        {recruiter.profile.nameRevealed ? (
                          <>
                            <h3>{recruiter.profile.name}</h3>
                            <span className="candidate-current-role">
                              {recruiter.profile.currentRole}
                            </span>
                          </>
                        ) : (
                          <>
                            <span
                              className="candidate-name-masked"
                              aria-label="Candidate name hidden"
                            />
                            <span className="candidate-current-role">
                              Name hidden until matched
                            </span>
                          </>
                        )}
                        <div className="candidate-location">
                          <EnvironmentOutlined /> {recruiter.profile.location}
                        </div>
                      </div>
                    </div>

                    <div className="candidate-tags">
                      {recruiter.profile.tags.map((tag) => (
                        <span className="tag-pill" key={tag}>
                          {tag}
                        </span>
                      ))}
                    </div>

                    <div className="candidate-experience">
                      <div className="about-heading">
                        {recruiter.profile.experienceHeading}
                      </div>
                      {recruiter.profile.experience.map((role) => (
                        <div className="candidate-role" key={role.title}>
                          <div className="candidate-role-icon">
                            <BankOutlined />
                          </div>
                          <div>
                            <div className="candidate-role-title">{role.title}</div>
                            {role.companyType?.length > 0 && (
                              <div className="candidate-role-company-type">
                                {role.companyType.map((label) => (
                                  <span key={label}>{label}</span>
                                ))}
                              </div>
                            )}
                            <div className="candidate-role-meta">{role.meta}</div>
                            {role.highlights?.length > 0 && (
                              <ul className="candidate-role-highlights">
                                {role.highlights.map((highlight) => (
                                  <li key={highlight}>{highlight}</li>
                                ))}
                              </ul>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Kept as a row even when locked, so the note and footer below
                  still line up with the candidate card's. */}
              {recruiter.state === "locked" ? (
                <div className="live-demo-card-action-slot" aria-hidden="true" />
              ) : (
                <button
                  type="button"
                  className="recruiter-card-action"
                  onClick={goToNextStep}
                >
                  {recruiter.actionLabel}
                </button>
              )}

              <p className="live-demo-note">
                <SafetyCertificateOutlined /> {recruiter.note}
              </p>

              <div className="live-demo-card-footer">
                <EyeOutlined /> {recruiter.footerBadge}
              </div>
            </div>
          </div>
        </div>

        <p className="live-demo-disclaimer">
          Illustrative example. Roles, profiles and companies shown are for
          demonstration only.
        </p>
      </div>
    </div>
  );
};

export default LiveDemo;
